// Details step component - Step 6 of character creation wizard

import { css, html } from 'lit';
import { getImageUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

const userIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;
// Inline lucide "dices" icon (static, trusted SVG; inherits currentColor).
const dicesIcon = () =>
  html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><rect width="12" height="12" x="2" y="10" rx="2" ry="2" /><path d="m17.92 14 3.5-3.5a2.24 2.24 0 0 0 0-3l-5-4.92a2.24 2.24 0 0 0-3 0L10 6" /><path d="M6 18h.01" /><path d="M10 14h.01" /><path d="M15 6h.01" /><path d="M18 9h.01" /></svg>`;

/**
 * @typedef {object} TokenListItem
 * @property {string} slug
 * @property {string} name
 * @property {string} [image_url]
 */

/**
 * Character details step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: update-name, select-token, randomize-name, randomize-token
 */
class DetailsStep extends BaseComponent {
  static properties = {
    name: { type: String },
    tokenId: { type: String, attribute: 'token-id' },
    tokens: { type: Array },
    tokensLoading: { type: Boolean, attribute: 'tokens-loading' },
    createError: { type: String, attribute: 'create-error' },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .selectable-card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .selectable-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .selectable-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .token-option {
      width: 100px;
      height: 100px;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      overflow: hidden;
    }

    .token-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .token-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #6c757d;
      color: white;
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }
  `;

  constructor() {
    super();
    this.name = '';
    this.tokenId = '';
    /** @type {Array<TokenListItem>} */
    this.tokens = [];
    this.tokensLoading = false;
    this.createError = '';
    this.isHeaderStuck = false;
  }

  render() {
    return html`
      <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
        <h5 class="card-title mb-0">Character Details</h5>
      </div>
      <p class="text-muted mb-3">Finish up by naming your character and choosing a portrait.</p>

      <!-- Character Name -->
      <div class="d-flex align-items-center mb-2">
        <h6 class="mb-0">Character Name</h6>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary ms-2 d-inline-flex align-items-center"
          data-bs-toggle="tooltip"
          data-bs-title="Random Name"
          @click="${this._onRandomizeName}"
        >
          ${dicesIcon()}
        </button>
      </div>
      <div class="mb-4">
        <input
          type="text"
          class="form-control"
          placeholder="Enter character name"
          maxlength="30"
          .value="${this.name}"
          @input="${(/** @type {Event} */ e) => this._onUpdateName(/** @type {HTMLInputElement} */ (e.target).value)}"
        />
        <div class="form-text">2-30 characters</div>
      </div>

      <!-- Token Selection -->
      <div class="d-flex align-items-center mb-2">
        <h6 class="mb-0">Token</h6>
        ${
          this.tokens.length > 0
            ? html`<button
              type="button"
              class="btn btn-sm btn-outline-secondary ms-2 d-inline-flex align-items-center"
              data-bs-toggle="tooltip"
              data-bs-title="Random Token"
              @click="${this._onRandomizeToken}"
            >
              ${dicesIcon()}
            </button>`
            : ''
        }
      </div>
      ${
        this.tokensLoading
          ? html`
            <div class="text-center py-3">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading tokens...</span>
              </div>
            </div>
          `
          : this.tokens.length === 0
            ? html` <div class="text-muted mb-4">No tokens available.</div> `
            : html`
              <div class="d-flex flex-wrap gap-2 mb-4">
                ${this.tokens.map(
                  (token) => html`
                    <div
                      class="selectable-card token-option ${this.tokenId === token.slug ? 'selected' : ''}"
                      title="${token.name}"
                      @click="${() => this._onSelectToken(token.slug)}"
                    >
                      ${
                        token.image_url
                          ? html` <img src="${getImageUrl(token.image_url)}" alt="${token.name}" class="token-image" /> `
                          : html` <div class="token-placeholder">${userIcon}</div> `
                      }
                    </div>
                  `,
                )}
              </div>
            `
      }
      ${this.createError ? html` <div class="alert alert-danger mt-3">${this.createError}</div> ` : ''}
    `;
  }

  /** @param {string} name */
  _onUpdateName(name) {
    this.dispatchEvent(new CustomEvent('update-name', { detail: { name }, bubbles: true, composed: true }));
  }

  /** @param {string} id */
  _onSelectToken(id) {
    this.dispatchEvent(new CustomEvent('select-token', { detail: { id }, bubbles: true, composed: true }));
  }

  _onRandomizeName() {
    this.dispatchEvent(new CustomEvent('randomize-name', { bubbles: true, composed: true }));
  }

  _onRandomizeToken() {
    this.dispatchEvent(new CustomEvent('randomize-token', { bubbles: true, composed: true }));
  }
}

customElements.define('details-step', DetailsStep);
